import { ERPGIcon } from "motw_core/UI/Icons/ERPGIcon";
import * as React from "react";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import RPGIcon from "source/app/presentation/Shared/RPGIcon/RPGIcon";
import { TouchActionButton } from "source/app/presentation/Shared/TouchActionButton/TouchActionButton";
import { IBookOfMindState } from "../../Helpers/IBookOfMindState";
import * as styles from "./SpeechControls.css";
import { StartSpeechLink } from "./StartSpeechLink";

export type BookOfMindPlaybackMode = "quote" | "page";

export function PlaybackModePicker({
    state,
    pageAudioAvailable,
    onSelect,
}: {
    state: IBookOfMindState;
    pageAudioAvailable: boolean;
    onSelect: (mode: BookOfMindPlaybackMode) => void;
}) {
    const [isOpen, setIsOpen] = React.useState(false);
    const pickerRef = React.useRef<HTMLSpanElement | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const closeOnOutsidePointer = (event: PointerEvent) => {
            if (!pickerRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("pointerdown", closeOnOutsidePointer);
        document.addEventListener("keydown", closeOnEscape);

        return () => {
            document.removeEventListener("pointerdown", closeOnOutsidePointer);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [isOpen]);

    const selectMode = (mode: BookOfMindPlaybackMode) => {
        if (mode === "page" && !pageAudioAvailable) {
            return;
        }

        setIsOpen(false);
        onSelect(mode);
    };

    return (
        <span className={styles.playbackModePicker} ref={pickerRef}>
            <StartSpeechLink
                state={state}
                whenClicked={() => setIsOpen((open) => !open)}
                tooltip="Choose playback mode"
                ariaLabel="Choose playback mode"
            />
            <span>
                <TouchActionButton
                    ariaLabel="Choose playback mode"
                    className={`${styles.controlButton} ${styles.iconOnlyControlButton}`}
                    onActivate={() => setIsOpen((open) => !open)}
                >
                    <Glyphicon
                        icon={
                            isOpen
                                ? EGlyphicon["chevron-down"]
                                : EGlyphicon["chevron-up"]
                        }
                    />
                </TouchActionButton>
            </span>
            {isOpen ? (
                <span className={styles.playbackModeMenu} role="menu">
                    <TouchActionButton
                        className={styles.playbackModeOption}
                        onActivate={() => selectMode("quote")}
                        ariaLabel="Play quotes"
                    >
                        <span className={styles.playbackModeOptionIcon}>
                            <Glyphicon icon={EGlyphicon.bullhorn} />
                        </span>
                        <span>Quote</span>
                    </TouchActionButton>
                    <TouchActionButton
                        className={styles.playbackModeOption}
                        onActivate={() => selectMode("page")}
                        ariaLabel={
                            pageAudioAvailable
                                ? "Play page audio"
                                : "Page audio requires a Google WaveNet voice"
                        }
                        disabled={!pageAudioAvailable}
                    >
                        <span className={styles.playbackModeOptionIcon}>
                            <RPGIcon icon={ERPGIcon["scroll-unfurled"]} />
                        </span>
                        <span>Page</span>
                    </TouchActionButton>
                </span>
            ) : null}
        </span>
    );
}
